import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { getFacebookTracking } from '@/hooks/useFacebookTracking';
import { useUserIp } from '@/hooks/useUserIp';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

// Facebook Pixel ID is public and safe to hardcode (visible in browser source code)
const FB_PIXEL_ID = '915167174494073';

// Generate unique event ID for deduplication
const generateEventId = () => {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

// Get UTM custom_data from sessionStorage
const getUtmCustomData = (): Record<string, string> => {
  try {
    const stored = sessionStorage.getItem('utm_params');
    if (stored) {
      const params = JSON.parse(stored);
      const data: Record<string, string> = {};
      if (params.utm_source) data.utm_source = params.utm_source;
      if (params.utm_medium) data.utm_medium = params.utm_medium;
      if (params.utm_campaign) data.utm_campaign = params.utm_campaign;
      if (params.utm_term) data.utm_term = params.utm_term;
      if (params.utm_content) data.utm_content = params.utm_content;
      return data;
    }
  } catch {
    // ignore
  }
  return {};
};

// Fire PageView via server-side CAPI (deduped with pixel)
const firePageViewCAPI = async (eventId: string, clientIpAddress?: string | null) => {
  const { fbc, fbp } = getFacebookTracking();
  const utmData = getUtmCustomData();

  try {
    await supabase.functions.invoke('fb-conversion', {
      body: {
        event_name: 'PageView',
        event_id: eventId,
        client_ip_address: clientIpAddress || undefined,
        client_user_agent: navigator.userAgent,
        fbc,
        fbp,
        event_source_url: window.location.href,
        custom_data: Object.keys(utmData).length > 0 ? utmData : undefined,
      },
    });
    console.log('[FB CAPI] PageView sent (server-side)', { eventId, hasIp: !!clientIpAddress });
  } catch (err) {
    console.error('[FB CAPI] PageView error:', err);
  }
};

const FacebookPixelInit = () => {
  const location = useLocation();
  const isInitialized = useRef(false);
  const isFirstPageView = useRef(true);
  const userIp = useUserIp();

  // Initialize Pixel with retry logic - defer to avoid blocking FCP
  useEffect(() => {
    if (!FB_PIXEL_ID || isInitialized.current) return;

    let attempts = 0;
    const maxAttempts = 50;

    const tryInit = () => {
      if (typeof window.fbq === 'function') {
        window.fbq('init', FB_PIXEL_ID);
        
        const eventId = generateEventId();
        window.fbq('track', 'PageView', {}, { eventID: eventId });
        isInitialized.current = true;
        isFirstPageView.current = false;

        console.log('[FB Pixel] Initialized with Advanced Matching, ID:', FB_PIXEL_ID);

        // Defer CAPI call to not compete with FCP resources
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => firePageViewCAPI(eventId, userIp));
        } else {
          setTimeout(() => firePageViewCAPI(eventId, userIp), 500);
        }
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryInit, 100);
      } else {
        console.warn('[FB Pixel] Failed to load after maximum attempts');
      }
    };

    tryInit();
  }, [userIp]);

  // Track PageView on route change (dual: pixel + CAPI)
  useEffect(() => {
    if (!isInitialized.current || isFirstPageView.current) return;

    if (typeof window.fbq === 'function') {
      const eventId = generateEventId();
      window.fbq('track', 'PageView', {}, { eventID: eventId });
      console.log('[FB Pixel] PageView on route change:', location.pathname);

      firePageViewCAPI(eventId, userIp);
    }
  }, [location.pathname, userIp]);

  return null;
};

export default FacebookPixelInit;
